import { get, omit } from 'lodash';

import { cacheableGet } from '@satellite/services/httpClient';
import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';

/**
 * @typedef {Object} AutocompleteOptions
 * @property {string} [search]
 * @property {string} [sortColumn]
 * @property {number} [page]
 * @property {number} [limit]
 * @property {string[]} [excludedOrgIds]
 * @property {string[]} [recentWarehouseIds]
 */

/**
 * @typedef {Object} SearchExpandedWarehouse
 * @property {string} id - Unique identifier for the warehouse.
 * @property {string} name - Name of the warehouse.
 * @property {string} facilityNumber - Warehouse facility number.
 * @property {string} street - Street address of the warehouse.
 * @property {string} city - City where the warehouse is located.
 * @property {string} state - State where the warehouse is located.
 * @property {string} zip - ZIP code of the warehouse.
 * @property {string} country - Country where the warehouse is located.
 * @property {string} address - Full address of the warehouse.
 * @property {string} orgId - Unique identifier for the organization managing the warehouse.
 * @property {string} orgName - Name of the organization managing the warehouse.
 * @property {boolean} isActive - Indicates if the warehouse is active.
 */

/**
 * @typedef {Object} WarehouseWithOrg
 * @property {string} id - Unique identifier for the warehouse.
 * @property {string} name - Name of the warehouse.
 * @property {string} facilityNumber - Warehouse facility number.
 * @property {string} street - Street address of the warehouse.
 * @property {string} city - City where the warehouse is located.
 * @property {string} state - State where the warehouse is located.
 * @property {string} zip - ZIP code of the warehouse.
 * @property {string} country - Country where the warehouse is located.
 * @property {string} address - Full address of the warehouse.
 * @property {Object} org - Organization details.
 * @property {string} org.id - Unique identifier for the organization managing the warehouse.
 * @property {string} org.name - Name of the organization managing the warehouse.
 */

/**
 * @param {SearchExpandedWarehouse} warehouse
 * @returns {WarehouseWithOrg}
 */
function normalizeSearchExpandedWarehouse(warehouse) {
  const result = omit(warehouse, 'orgId', 'orgName', 'isActive');
  return { ...result, org: { id: warehouse.orgId, name: warehouse.orgName } };
}

const defaultRequestOptions = {
  fields: [
    'name',
    'street',
    'city',
    'state',
    'country',
    'zip',
    'facilityNumber',
    'timezone',
    'settings',
    'schedule',
    'geolocation'
  ],
  joins: []
};

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

export default {
  async getWarehouseById(warehouseId, params, options = {}, requestConfig = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(
      `warehouse/${warehouseId}?${queryString}`,
      {
        params
      },
      requestConfig
    );
    return getResponse(response, options);
  },
  async getWarehouses(params, options = {}, requestConfig = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`warehouse?${queryString}`, { params, ...requestConfig });
    return getResponse(response, options);
  },

  /**
   * @template TResponse
   * Fetch warehouses with cacheable HTTP client and flexible response handling.
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getWarehousesV2({
    query = {},
    getResponseConfig = {},
    requestConfig = {},
    enableCache = true
  } = {}) {
    const queryString = getQueryStringFromOptions(query);

    const axiosResponse = await cacheableGet(`warehouse?${queryString}`, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  async getListWarehouses(params, options = {}, requestConfig = {}) {
    const fieldsAndJoins = {
      fields: options?.fields ? [...options.fields] : [...this.requestOptions.list.fields],
      joins: options?.joins ? [...options.joins] : [...this.requestOptions.list.joins]
    };
    const response = await this.getWarehouses(
      params,
      {
        ...fieldsAndJoins,
        includeMetaData: options?.includeMetaData
      },
      requestConfig
    );
    return response;
  },
  requestOptions: {
    default: {
      field: defaultRequestOptions.fields,
      joins: defaultRequestOptions.joins
    },
    list: {
      fields: [...defaultRequestOptions.fields],
      joins: [...defaultRequestOptions.joins, 'org||name']
    },
    grid: {
      fields: [...defaultRequestOptions.fields],
      joins: [
        ...defaultRequestOptions.joins,
        'docks||name,loadTypeIds,isActive,capacityParentId,schedule,sortOrder',
        'docks.capacityChildren||name,isActive,capacityParentId,loadTypeIds,schedule,sortOrder',
        'org||name,settings'
      ]
    }
  },
  /**
   * PNG of the geofence circle around the warehouse's stored geolocation. Neutron renders it so
   * the Google key stays server-side. Quiet on failure: it is only a preview, so callers hide it
   * rather than show an error toast.
   * @param {string} warehouseId
   * @param {{ radius: number, unit: import('@nova/core').GeofenceRadiusUnit }} geofence
   * @returns {Promise<Blob>}
   */
  async getGeofenceMap(warehouseId, { radius, unit }) {
    const response = await axios.get(`warehouse/${warehouseId}/geofence-map`, {
      params: { radius, unit },
      responseType: 'blob',
      suppressNotification: true
    });
    return response.data;
  },
  async getWarehouseDocks(warehouseId, params = {}, options = {}) {
    params.s = { ...params.s, warehouseId };
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`dock?${queryString}`, {
      params
    });
    return getResponse(response, options);
  },
  async createLoadTypesCustomForms(warehouseId, loadTypeIds, formName, options = {}) {
    const response = await axios.post(`warehouse/${warehouseId}/custom-forms`, {
      loadTypeIds,
      formName
    });
    return getResponse(response, options);
  },
  async updateLoadTypesCustomForms(warehouseId, parentCode, loadTypeIds, formName, options = {}) {
    const response = await axios.patch(`warehouse/${warehouseId}/custom-forms`, {
      parentCode,
      loadTypeIds,
      formName
    });
    return getResponse(response, options);
  },
  /**
   * @param {AutocompleteOptions} [options={}] - The optional parameters for autocompletion.
   * @returns {Promise<{items: WarehouseWithOrg[], count: number, total: number, totalPages: number, page: number}>} - The autocomplete results.
   */
  async autocomplete(options = {}) {
    const defaultLimit = 15;
    const { limit, page, search } = {
      limit: defaultLimit,
      page: 1,
      search: '',
      ...options
    };

    async function withSearchExpanded() {
      const offset = (page - 1) * limit;
      const payload = {
        search,
        excludedOrgIds: options.excludedOrgIds,
        recentWarehouseIds: options.recentWarehouseIds,
        pagination: { limit, offset }
      };

      try {
        const response = await axios.post('/warehouse-search/autocomplete', payload);
        const items = get(response, 'data.items', []) ?? [];
        const total = get(response, 'data.meta.totalRecords', 0) ?? 0;
        const totalPages = get(response, 'data.meta.totalPages', 0) ?? 0;
        return {
          items: items.map(normalizeSearchExpandedWarehouse),
          count: items.length,
          page,
          total,
          totalPages
        };
      } catch (err) {
        return { items: [], count: 0, total: 0 };
      }
    }

    return withSearchExpanded();
  }
};
